/* =========================================================================
   LandingPage11 — "Build Your Own Online System With AI".

   Content comes from the client's PDF of the same name; its sentences are kept
   verbatim wherever they work as landing copy, and its section order is kept
   almost intact:

     hero → what's already built → website vs. system → tell AI what you want →
     idea to application → what can you build → rent vs. hire vs. own →
     built for people → mission → CTA

   Two of the PDF's beats are stated twice in the source and merged here:
   "More Than Just a Website" (p1) folds into "Website vs. Online System" (p3),
   and "You Bring the Idea. AI Helps Build the System." (p3) becomes the eyebrow
   over "Built for People, Not Just Developers".

   One band is NOT from the PDF: "rent vs. hire vs. own" is LandingPage10's
   "why your own" comparison, carried over at the client's request — except
   that its first card is LandingPage9's "Go to college" where LP10 has "Rent
   software", and its third quotes our price where LP10 says "Learn it once".
   Its copy was copied into lp11.why* rather than read from lp10.* / lp9.*, so
   this page still renders nothing but its own namespace.

   RELATIONSHIP TO LandingPage10. Both sell an online system, but not the same
   way, and they are meant to be compared:
     LP10  the OUTCOME — a system you own — from the client's one-line mission
     LP11  the PRODUCT — a ready-made application foundation you point AI at
   LP11 also carries the PDF's own mission statement, which is worded
   differently from the one LP10 uses. Neither page is derived from the other:
   they share no copy keys and no stylesheet, and the two things they do share
   on purpose were both brought over from LP10 at the client's request — the
   squared blueprint texture (on the hero, "already built", "what can you
   build" and the mission band) and the rent / hire / own band. Everything
   else still differs.

   NOTE: the PDF never mentions the course, its lessons, or the AI tutor — it
   sells the foundation plus AI. This page follows the PDF and stays silent on
   them too, with one exception the client asked for: the rent / hire / own
   band's third card quotes the founding price ("$497 course"), as
   LandingPage9's anchor card does. That price has to match what
   /subscriptions shows (lp6.priceAmount), and lp11Copy.test.js checks it.
   Both CTAs go to /contact — this page opens a conversation rather than a
   checkout — so the price is a comparison, not a promise of a seat, and there
   is no ENROLLMENT_OPEN branch to worry about.

   Copy is its own lp11.* namespace in BOTH locales with no fallback chain, so
   a key missing from one locale renders as a raw "lp11.x"; lp11Copy.test.js
   keeps the two blocks in lockstep.

   Everything is scoped under .lp11. LandingPage6-10 are untouched.
   ========================================================================= */

import React, { useEffect, useRef } from "react";

// reactstrap components
import { Container, Row, Col } from "reactstrap";

import "./LandingPage11.css";

// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faDatabase,
  faUserPlus,
  faShieldHalved,
  faListCheck,
  faChartLine,
  faSliders,
  faGears,
  faCloudArrowUp,
  faLightbulb,
  faFileLines,
  faDesktop,
  faWandMagicSparkles,
  faRobot,
  faGraduationCap,
  faUserTie,
  faInfinity,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import useReveal from "hooks/useReveal";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* The eight parts a real web application needs, straight from the PDF. Shared
   by the hero stack (first five, plus a "+3 more" line) and the section that
   lists all eight — one source so the two can never drift apart. */
const PARTS = [
  { n: 1, icon: faDatabase },
  { n: 2, icon: faUserPlus },
  { n: 3, icon: faShieldHalved },
  { n: 4, icon: faListCheck },
  { n: 5, icon: faChartLine },
  { n: 6, icon: faSliders },
  { n: 7, icon: faGears },
  { n: 8, icon: faCloudArrowUp },
];
const HERO_PARTS = PARTS.slice(0, 5);

// The things a customer can DO once it's a system rather than a website.
const VS_ACTIONS = [1, 2, 3, 4, 5, 6];

// The five example prompts, verbatim from the PDF.
const PROMPTS = [1, 2, 3, 4, 5];

// From idea to working application.
const STEPS = [1, 2, 3, 4, 5];

// What can you build — 11 items; the last one ("And much more") is styled as a
// trailing ellipsis rather than as another product.
const BUILDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const BUILD_MORE = 11;

// College / pay an engineer every time / own it — LandingPage10's band, with
// LandingPage9's college card in the first slot. The two losing options carry
// a cross, the third a check.
const CHOICES = [
  { n: 1, icon: faGraduationCap, win: false },
  { n: 2, icon: faUserTie, win: false },
  { n: 3, icon: faInfinity, win: true },
];

// "01", "02"... for the mono indices and step numbers. Formatting only — the
// numbers are the existing loop indices, not copy.
const pad2 = (n) => String(n).padStart(2, "0");

// Reveal stagger for the nth sibling.
const delay = (i, step = 70) => ({ "--reveal-delay": `${i * step}ms` });

// The trailing arrow on the two "talk to us" buttons. Decorative: the button's
// text is its accessible name.
const ArrowIcon = () => (
  <svg
    className="lp11-btn-arrow"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M3.5 8h9M8.5 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LandingPage11 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useReveal();

  // Transparent navbar over the hero, fading to solid navy on scroll. Body
  // classes are cleaned up on unmount so every other page keeps the solid one.
  useEffect(() => {
    document.body.classList.add("lp11-active");
    const onScroll = () => {
      document.body.classList.toggle("lp11-nav-solid", window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.body.classList.remove("lp11-active", "lp11-nav-solid");
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  /* Both CTAs are a conversation, not a checkout: what this page sells is a
     foundation someone points AI at, and that starts with them telling us what
     they want to build. /subscriptions is reachable from the navbar. */
  const goContact = () => navigate("/contact");
  // Focus follows the scroll, so keyboard and screen-reader users continue
  // from the section they were sent to.
  const scratchTitleRef = useRef(null);
  const scrollToScratch = () => {
    const el = document.getElementById("lp11-scratch");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    scratchTitleRef.current?.focus({ preventScroll: true });
  };

  return (
    <>
      <DemoNavbar />
      <main className="lp11">
        {/* ----------------------------- HERO ----------------------------- */}
        <section className="lp11-hero lp11-grid-dark">
          <span className="cn-grain" aria-hidden="true" />
          <span className="lp11-orb lp11-orb-1" aria-hidden="true" />
          <span className="lp11-orb lp11-orb-2" aria-hidden="true" />
          <span className="lp11-orb lp11-orb-3" aria-hidden="true" />

          <Container>
            <Row>
              <Col lg="7">
                <span className="lp11-eyebrow" data-reveal>
                  <span className="dot" />
                  {t("lp11.eyebrow")}
                </span>
                <h1
                  className="lp11-title"
                  data-reveal
                  style={{ "--reveal-delay": "60ms" }}
                >
                  {t("lp11.heroTitle")}
                </h1>
                <p
                  className="lp11-lead"
                  data-reveal
                  style={{ "--reveal-delay": "120ms" }}
                >
                  {t("lp11.heroLead")}
                </p>

                <div
                  className="lp11-actions"
                  data-reveal
                  style={{ "--reveal-delay": "180ms" }}
                >
                  <button className="lp11-btn-primary" onClick={goContact}>
                    {t("lp11.heroPrimary")}
                    <ArrowIcon />
                  </button>
                  <button className="lp11-btn-ghost" onClick={scrollToScratch}>
                    {t("lp11.heroSecondary")}
                  </button>
                </div>

                <div
                  className="lp11-pills"
                  data-reveal
                  style={{ "--reveal-delay": "240ms" }}
                >
                  <span className="lp11-pill">{t("lp11.pill1")}</span>
                  <span className="lp11-pill">{t("lp11.pill2")}</span>
                  <span className="lp11-pill">{t("lp11.pill3")}</span>
                </div>
              </Col>

              {/* The whole pitch in one picture: a dashed, empty layer for the
                  student's idea sitting on top of ticked layers they are given
                  rather than build — drawn as an app window, with a second
                  card peeking out behind it for depth. */}
              <Col lg="5">
                <div
                  className="lp11-stack-wrap"
                  data-reveal
                  style={{ "--reveal-delay": "160ms" }}
                >
                  <div className="lp11-stack-tilt">
                    <span className="lp11-stack-ghost" aria-hidden="true" />
                    <div className="lp11-stack">
                      <div className="lp11-stack-head">
                        <span className="lp11-stack-lights" aria-hidden="true">
                          <span />
                          <span />
                          <span />
                        </span>
                        <span className="lp11-stack-title">
                          {t("lp11.stackTitle")}
                        </span>
                      </div>

                      <div className="lp11-stack-body">
                        <div className="lp11-slab-yours">
                          <FontAwesomeIcon icon={faLightbulb} />
                          {t("lp11.stackYours")}
                        </div>

                        {HERO_PARTS.map((p) => (
                          <div className="lp11-slab" key={p.n}>
                            <span className="lp11-slab-check">
                              <FontAwesomeIcon icon={faCheck} />
                            </span>
                            {t(`lp11.part${p.n}`)}
                          </div>
                        ))}
                      </div>

                      <div className="lp11-stack-foot">
                        <p className="lp11-stack-more">{t("lp11.stackMore")}</p>
                        <span className="lp11-stack-ready">
                          <span className="lp11-stack-dot" />
                          {t("lp11.stackReady")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------- ALREADY BUILT FOR YOU --------------------- */}
        <section className="lp11-scratch lp11-grid-bg on-wash" id="lp11-scratch">
          <Container>
            <div className="lp11-head" data-reveal>
              <span className="lp11-eyebrow-2">{t("lp11.scratchEyebrow")}</span>
              <h2 ref={scratchTitleRef} tabIndex={-1}>{t("lp11.scratchTitle")}</h2>
              <p>{t("lp11.scratchSubtitle")}</p>
            </div>

            <Row>
              {PARTS.map((p, i) => (
                <Col
                  lg="3"
                  md="6"
                  className="lp11-col"
                  key={p.n}
                  data-reveal
                  style={delay(i, 55)}
                >
                  <div className="lp11-part">
                    <div className="lp11-part-top">
                      <span className="lp11-part-check">
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                      <span className="lp11-part-n" aria-hidden="true">
                        {pad2(p.n)}
                      </span>
                    </div>
                    <span className="lp11-part-label">
                      {t(`lp11.part${p.n}`)}
                    </span>
                  </div>
                </Col>
              ))}
            </Row>

            <p className="lp11-scratch-note" data-reveal>
              {t("lp11.scratchNote")}
            </p>
          </Container>
        </section>

        {/* ------------------ WEBSITE VS. ONLINE SYSTEM -------------------- */}
        <section className="lp11-vs">
          <Container>
            <div className="lp11-head lp11-head-center" data-reveal>
              <span className="lp11-eyebrow-2">{t("lp11.vsEyebrow")}</span>
              <h2>{t("lp11.vsTitle")}</h2>
              <p>{t("lp11.vsSubtitle")}</p>
            </div>

            <Row className="lp11-vs-grid">
              <Col lg="6" className="lp11-col" data-reveal>
                <div className="lp11-vs-card plain">
                  <div className="lp11-vs-ic">
                    <FontAwesomeIcon icon={faFileLines} />
                  </div>
                  <h3>{t("lp11.vsWebsiteLabel")}</h3>
                  <p>{t("lp11.vsWebsiteBody")}</p>
                </div>
              </Col>

              <Col
                lg="6"
                className="lp11-col"
                data-reveal
                style={{ "--reveal-delay": "90ms" }}
              >
                <div className="lp11-vs-card rich">
                  <span className="cn-grain" aria-hidden="true" />
                  <div className="lp11-vs-ic">
                    <FontAwesomeIcon icon={faDesktop} />
                  </div>
                  <h3>{t("lp11.vsSystemLabel")}</h3>
                  <p>{t("lp11.vsSystemBody")}</p>
                  <div className="lp11-vs-actions">
                    {VS_ACTIONS.map((n) => (
                      <span className="lp11-vs-action" key={n}>
                        <FontAwesomeIcon icon={faCheck} />
                        {t(`lp11.vsAction${n}`)}
                      </span>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------- TELL AI WHAT YOU WANT --------------------- */}
        {/* The example prompts are the most persuasive thing in the PDF, so
            they get the page's centrepiece treatment: a real conversation,
            framed as a chat window. */}
        <section className="lp11-prompts">
          <span className="cn-grain" aria-hidden="true" />
          <Container>
            <div className="lp11-head lp11-head-center lp11-head-dark" data-reveal>
              <span className="lp11-eyebrow-2 lp11-eyebrow-light">
                {t("lp11.promptsEyebrow")}
              </span>
              <h2>{t("lp11.promptsTitle")}</h2>
              <p>{t("lp11.promptsSubtitle")}</p>
            </div>

            <div className="lp11-chat" data-reveal>
              <div className="lp11-chat-head" aria-hidden="true">
                <span className="lp11-chat-lights">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="lp11-chat-bar" />
              </div>

              <div className="lp11-chat-body">
                {PROMPTS.map((n, i) => (
                  <div
                    className="lp11-bubble-row you"
                    key={n}
                    data-reveal
                    style={delay(i + 1, 90)}
                  >
                    <div className="lp11-bubble you">{t(`lp11.prompt${n}`)}</div>
                  </div>
                ))}

                <div
                  className="lp11-bubble-row"
                  data-reveal
                  style={delay(PROMPTS.length + 1, 90)}
                >
                  <div className="lp11-bubble ai">
                    <span className="lp11-bubble-ic">
                      <FontAwesomeIcon icon={faRobot} />
                    </span>
                    <span>{t("lp11.promptsNote")}</span>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ---------------- FROM IDEA TO WORKING APPLICATION --------------- */}
        <section className="lp11-steps">
          <Container>
            <div className="lp11-head lp11-head-center" data-reveal>
              <h2>{t("lp11.stepsTitle")}</h2>
              <p>{t("lp11.stepsSubtitle")}</p>
            </div>

            <div className="lp11-timeline">
              {STEPS.map((n, i) => (
                <div
                  className="lp11-tl-item"
                  key={n}
                  data-reveal
                  style={delay(i, 60)}
                >
                  <span className="lp11-tl-num">{pad2(n)}</span>
                  <div className="lp11-tl-body">
                    <h3>{t(`lp11.step${n}T`)}</h3>
                    <p>{t(`lp11.step${n}P`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* --------------------- WHAT CAN YOU BUILD? ----------------------- */}
        <section className="lp11-build lp11-grid-bg">
          <Container>
            <div className="lp11-head" data-reveal>
              <span className="lp11-eyebrow-2">{t("lp11.buildEyebrow")}</span>
              <h2>{t("lp11.buildTitle")}</h2>
              <p>{t("lp11.buildSubtitle")}</p>
            </div>

            <div className="lp11-chips">
              {BUILDS.map((n, i) => (
                <span
                  className={`lp11-chip${n === BUILD_MORE ? " more" : ""}`}
                  key={n}
                  data-reveal
                  style={delay(i, 40)}
                >
                  {t(`lp11.build${n}`)}
                </span>
              ))}
            </div>

            <p className="lp11-build-note" data-reveal>
              {t("lp11.buildNote")}
            </p>
          </Container>
        </section>

        {/* ------------------------- WHY YOUR OWN -------------------------- */}
        {/* LandingPage10's band, with LandingPage9's college card first. Sits
            between two light bands so it never runs into the dark mission
            band below. */}
        <section className="lp11-why">
          <span className="cn-grain" aria-hidden="true" />
          <Container>
            <div className="lp11-head lp11-head-center lp11-head-dark" data-reveal>
              <span className="lp11-eyebrow-2 lp11-eyebrow-light">
                {t("lp11.whyEyebrow")}
              </span>
              <h2>{t("lp11.whyTitle")}</h2>
              <p>{t("lp11.whySubtitle")}</p>
            </div>

            <Row className="lp11-why-grid">
              {CHOICES.map((c, i) => (
                <Col
                  lg="4"
                  md="6"
                  className="lp11-col"
                  key={c.n}
                  data-reveal
                  style={delay(i, 80)}
                >
                  <div className={`lp11-why-card${c.win ? " win" : ""}`}>
                    <span
                      className={`lp11-why-mark ${c.win ? "check" : "cross"}`}
                    >
                      <FontAwesomeIcon icon={c.win ? faCheck : faXmark} />
                      <span className="sr-only">
                        {t(c.win ? "a11yPages.recommended" : "a11yPages.notRecommended")}
                      </span>
                    </span>
                    <div className="lp11-why-ic">
                      <FontAwesomeIcon icon={c.icon} />
                    </div>
                    <h3>{t(`lp11.why${c.n}Label`)}</h3>
                    <div className="lp11-why-cost">{t(`lp11.why${c.n}Cost`)}</div>
                    <p>{t(`lp11.why${c.n}Note`)}</p>
                  </div>
                </Col>
              ))}
            </Row>

            <p className="lp11-why-punch" data-reveal>
              {t("lp11.whyPunch")}
            </p>
          </Container>
        </section>

        {/* ------------ BUILT FOR PEOPLE, NOT JUST DEVELOPERS -------------- */}
        <section className="lp11-people">
          <Container>
            <Row>
              <Col lg="7" data-reveal>
                <span className="lp11-eyebrow-2">{t("lp11.peopleEyebrow")}</span>
                <h2>{t("lp11.peopleTitle")}</h2>
                <p>{t("lp11.peopleBody")}</p>
              </Col>
              <Col lg="5" data-reveal style={{ "--reveal-delay": "90ms" }}>
                <div className="lp11-people-quote">{t("lp11.peopleGoal")}</div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* ---------------------------- MISSION ---------------------------- */}
        <section className="lp11-mission lp11-grid-dark">
          <span className="cn-grain" aria-hidden="true" />
          <Container>
            <div className="lp11-mission-inner" data-reveal>
              <span className="lp11-eyebrow-2 lp11-eyebrow-light">
                {t("lp11.missionEyebrow")}
              </span>
              <div className="lp11-mission-rule" />
              <p>{t("lp11.missionBody")}</p>
            </div>
          </Container>
        </section>

        {/* --------------------------- FINAL CTA --------------------------- */}
        <section className="lp11-cta">
          <Container>
            <div className="lp11-cta-card" data-reveal>
              <span className="cn-grain" aria-hidden="true" />
              <Row>
                <Col lg="8">
                  <h2>{t("lp11.finalTitle")}</h2>
                  <ul className="lp11-cta-steps">
                    {[1, 2, 3, 4].map((n) => (
                      <li key={n}>
                        <span className="lp11-cta-step-ic">
                          <FontAwesomeIcon icon={faWandMagicSparkles} />
                        </span>
                        {t(`lp11.finalStep${n}`)}
                      </li>
                    ))}
                  </ul>
                </Col>
                <Col lg="4" className="ml-lg-auto">
                  <button className="lp11-cta-btn" onClick={goContact}>
                    {t("lp11.finalCta")}
                    <ArrowIcon />
                  </button>
                </Col>
              </Row>
            </div>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default LandingPage11;
